import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Send, Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        toast.success("Message sent successfully!", {
          description: "We'll get back to you within 24 hours.",
        });
        setFormData({
          name: "",
          email: "",
          phone: "",
          service: "",
          message: "",
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to send message", {
        description: "Please try again or call us directly.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      label: "Phone",
      value: "778-345-5884",
      href: "tel:+17783455884",
    },
    {
      icon: Mail,
      label: "Email",
      value: "contact@jntile.ca",
      href: "mailto:contact@jntile.ca",
    },
    {
      icon: MapPin,
      label: "Address",
      value: "6002 Gladwin Rd. Abbotsford, BC V4X 1V9",
      href: "https://maps.google.com/?q=6002+Gladwin+Rd+Abbotsford+BC+V4X+1V9",
    },
    {
      icon: Clock,
      label: "Hours",
      value: "Mon-Fri: 8am - 5pm",
      href: null,
    },
  ];

  const services = [
    "Kitchen Renovation",
    "Bathroom Remodeling",
    "Full Home Renovation",
    "Flooring Installation",
    "Painting Services",
    "Other",
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-[#0f172a]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <p className="text-[#d97706] font-['Barlow_Condensed'] font-semibold uppercase tracking-widest mb-4">
              Contact Us
            </p>
            <h1 className="font-['Barlow_Condensed'] text-5xl md:text-6xl font-bold text-white uppercase tracking-tight mb-6">
              Let's Start Your Project
            </h1>
            <p className="text-xl text-slate-400 max-w-2xl">
              Ready to transform your home? Get in touch for a free consultation and estimate. We're here to bring your vision to life.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 md:py-28 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div className="bg-white p-8 md:p-10 border border-slate-200">
              <h2 className="font-['Barlow_Condensed'] text-2xl md:text-3xl font-bold text-[#0f172a] uppercase tracking-tight mb-2">
                Send Us a Message
              </h2>
              <p className="text-slate-600 mb-8">
                Fill out the form below and we'll get back to you within 24 hours.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-slate-700 font-medium">
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="John Smith"
                      className="h-12 border-slate-300 focus:border-[#d97706] focus:ring-[#d97706]"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-700 font-medium">
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="john@example.com"
                      className="h-12 border-slate-300 focus:border-[#d97706] focus:ring-[#d97706]"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-slate-700 font-medium">
                      Phone Number
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="(555) 123-4567"
                      className="h-12 border-slate-300 focus:border-[#d97706] focus:ring-[#d97706]"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="service" className="text-slate-700 font-medium">
                      Service Interested In
                    </Label>
                    <Select onValueChange={handleServiceChange} value={formData.service}>
                      <SelectTrigger 
                        className="h-12 border-slate-300 focus:border-[#d97706] focus:ring-[#d97706]"
                        data-testid="contact-service-select"
                      >
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map((service) => (
                          <SelectItem key={service} value={service}>
                            {service}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-slate-700 font-medium">
                    Project Details *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    placeholder="Tell us about your project, timeline, and any specific requirements..."
                    rows={5}
                    className="border-slate-300 focus:border-[#d97706] focus:ring-[#d97706] resize-none"
                    data-testid="contact-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#d97706] hover:bg-[#b45309] text-white font-['Barlow_Condensed'] font-bold uppercase tracking-wider h-14 btn-glow"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 w-5 h-5" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Contact Info & Map */}
            <div className="space-y-8">
              {/* Contact Info Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {contactInfo.map((item, index) => (
                  <div 
                    key={index}
                    className="bg-white p-6 border border-slate-200"
                    data-testid={`contact-info-${item.label.toLowerCase()}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#d97706]/10 flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-6 h-6 text-[#d97706]" />
                      </div>
                      <div>
                        <p className="font-['Barlow_Condensed'] font-bold text-[#0f172a] uppercase tracking-wider text-sm mb-1">
                          {item.label}
                        </p>
                        {item.href ? (
                          <a 
                            href={item.href}
                            target={item.href.startsWith("http") ? "_blank" : undefined}
                            rel={item.href.startsWith("http") ? "noopener noreferrer" : undefined}
                            className="text-slate-600 hover:text-[#d97706] transition-colors"
                          >
                            {item.value}
                          </a>
                        ) : (
                          <p className="text-slate-600">{item.value}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Map */}
              <div className="bg-white border border-slate-200 overflow-hidden">
                <div className="p-6 border-b border-slate-200">
                  <h3 className="font-['Barlow_Condensed'] font-bold text-[#0f172a] uppercase tracking-wider">
                    Our Location
                  </h3>
                </div>
                <div className="aspect-[4/3]">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2615.8845736651826!2d-122.37847492346!3d49.05026067134!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x5485c3f4e3a8d8c5%3A0x4c4b8e4b8e4b8e4b!2s6002%20Gladwin%20Rd%2C%20Abbotsford%2C%20BC%20V4X%201V9!5e0!3m2!1sen!2sca!4v1699999999999!5m2!1sen!2sca"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="J.N Nickel Contracting Location"
                    data-testid="contact-map"
                  />
                </div>
              </div>

              {/* Service Area */}
              <div className="bg-[#0f172a] p-8">
                <h3 className="font-['Barlow_Condensed'] font-bold text-white text-xl uppercase tracking-wider mb-4">
                  Service Area
                </h3>
                <p className="text-slate-400 leading-relaxed">
                  We proudly serve communities in the Fraser Valley from Horseshoe Bay to Hope.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
